import * as React from 'react';
import { Alert, Button, Input, Modal, Panel, Select } from '@ixicare/ui';
import { Beacon, Device, DeviceEntryProps, DevicePatchBody, DeviceType, Gateway } from '@ixicare/resources';
import { t } from '~i18n';
import { useMutation } from '@tanstack/react-query';
import { getFieldError, humaniseErrors } from '~utils/error';
import { Spacer } from '~components/Utils/Spacer';
import { DBGWithType } from '~modules/devices/components/DeviceHealthTable/types';
import connect from '~/store/connect';
import SiteStore from '~modules/sites/store/Site';
import { CALLABLE_TYPES, WEARABLE_OR_BUTTON_TYPES } from '~modules/devices/utils/constants';
import { buttonOptions } from './CreateInstalledDeviceModal';
import styles from '~/components/ActionBar/ActionBar.module.css';
import classnames from 'classnames';

const MAX_CHIPS = 7;

interface UpdateDeviceModalProps {
  onClose: () => void;
  onRefresh: () => void;
  type: DeviceType;
  devices: DBGWithType[];
  showSiteSelect?: boolean;
  siteOnly?: boolean;
  showMoveHint?: boolean;
  sites?: SiteStore[];
}

function UpdateDeviceModalComponent({
  onClose,
  onRefresh,
  type,
  devices,
  showSiteSelect,
  siteOnly,
  showMoveHint,
  sites,
}: UpdateDeviceModalProps) {
  const device = devices[0];
  const typeTranslation = React.useMemo(() => type && t(`device.types.${type.toLowerCase()}`), [type]);
  const [description, setDescription] = React.useState(device.description ?? '');
  const [phoneNumber, setPhoneNumber] = React.useState((device as DeviceEntryProps)?.phoneNumber ?? '');
  const [emergencyButton, setEmergencyButton] = React.useState(
    (device as DeviceEntryProps).subType ?? 'REGULAR_BUTTON'
  );
  const [selectedSiteId, setSelectedSiteId] = React.useState<number | undefined>(device.siteId);
  const siteOptions = React.useMemo(
    () =>
      (sites ?? []).map((site) => ({
        label: site.name,
        value: site.id,
      })),
    [sites]
  );
  const isDevice = WEARABLE_OR_BUTTON_TYPES.includes(type);
  const changed: DevicePatchBody = {
    ...(description !== (device.description ?? '') && { description }),
    ...(phoneNumber !== ((device as DeviceEntryProps).phoneNumber ?? '') && { phoneNumber }),
    ...(isDevice &&
      emergencyButton !== ((device as DeviceEntryProps).subType ?? 'REGULAR_BUTTON') && {
        subType: emergencyButton === 'EMERGENCY_BUTTON' ? 'EMERGENCY_BUTTON' : null,
      }),
    ...(isDevice && selectedSiteId !== device.siteId && { siteId: selectedSiteId }),
  };

  const updateMutation = useMutation({
    mutationFn: async () => {
      await Promise.all(devices.map((d) => updateDevice(d.type, d.id, changed)));
    },
    onSuccess: () => {
      onRefresh();
      onClose();
    },
  });

  return (
    <Modal style={{ maxWidth: 450 }}>
      <>
        <h3 className="text-center mb-30">{t('device.form.update', { type: typeTranslation })}</h3>
        <ul className={styles.chips}>
          {devices.slice(0, MAX_CHIPS).map((d) => (
            <li className={classnames('text-center', 'mb-10', styles.chip)} key={d.macAddress}>
              {d.macAddress} - {d.description}
            </li>
          ))}
          {devices.length > MAX_CHIPS && (
            <li className={classnames('text-center', styles.chip)}>+{devices.length - MAX_CHIPS}</li>
          )}
        </ul>
        {updateMutation.error && <Alert type="error">{humaniseErrors(updateMutation.error as Error).title}</Alert>}
        {!siteOnly && (
          <>
            <Input
              label={t('device.form.description')}
              value={description}
              onValueChange={(x) => {
                setDescription(x);
              }}
              error={getFieldError('description', updateMutation.error as Error)}
            />
            <Spacer size={32} />
            {CALLABLE_TYPES.includes(type) && (
              <>
                <Input
                  label={t('global.phoneNumber')}
                  value={phoneNumber}
                  onValueChange={setPhoneNumber}
                  error={getFieldError('phoneNumber', updateMutation.error as Error)}
                />
                <Spacer size={32} />
              </>
            )}
            {type == 'WALL_MOUNTED_DISTRESS_BUTTON' && (
              <>
                <Panel.Subtitle>{t('device.form.subtype')}</Panel.Subtitle>

                <Select
                  value={emergencyButton}
                  onChange={(value) => {
                    setEmergencyButton(value);
                  }}
                  options={buttonOptions}
                />

                <Spacer size={32} />
              </>
            )}
          </>
        )}
        {showSiteSelect && (
          <>
            <Select
              label={t('device_config.general.assigned_site')}
              value={selectedSiteId}
              onChange={(value) => {
                setSelectedSiteId(value);
              }}
              options={siteOptions}
            />
            <Spacer size={32} />
          </>
        )}
        {showMoveHint && <p className="text-muted mb-15">{t('device.form.moveArchivedOnly')}</p>}
        <Button
          className="mb-15 error"
          disabled={updateMutation.isPending || (!siteOnly && !description.length)}
          loading={updateMutation.isPending}
          isBlock
          buttonType="success"
          type="button"
          onClick={() => (Object.keys(changed).length === 0 ? onClose() : updateMutation.mutate())}
        >
          {t('device.form.confirm')}
        </Button>
        <Button onClick={() => onClose()} disabled={updateMutation.isPending} isBlock buttonType="link">
          {t('global.cancel')}
        </Button>
      </>
    </Modal>
  );
}

export const UpdateDeviceModal = connect((state: any) => ({
  sites: state.sites.sites,
}))(UpdateDeviceModalComponent);

const updateDevice = async (type: DeviceType, id: number, changed: DevicePatchBody) => {
  if (type === DeviceType.BEACON) {
    await Beacon.patchBeacon(id, changed);
  }
  if (type === DeviceType.GATEWAY) {
    await Gateway.patchGateway(id, changed);
  }
  if (WEARABLE_OR_BUTTON_TYPES.includes(type)) {
    await Device.patchDevice(id, changed);
  }
};
